import { IsoDateTime, IsoDateTimeTz, IsoDuration, IsoTime, IsoTimeTz } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';
import { addMillisecondsToDuration } from '../../helper/add-milliseconds-to-duration.function';
import { SECONDS_IN_HOUR } from '../../helper/duration.constants';
import { addMillisecondsToTime } from '../../helper/add-milliseconds-to-time.function';

/**
 * Add a number of hours to an ISO date-time string (with or without timezone) or to an ISO 8601 duration.
 *
 * Supported inputs:
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz suffix
 * - ISO 8601 Duration: P... -> returns a duration string
 *
 * Notes:
 * - For date-only inputs (YYYY-MM-DD), time-based addition is not allowed and will throw.
 * - amount must be a finite integer; can be negative.
 */
export function addHours<T extends IsoDateTime | IsoDateTimeTz | IsoTime | IsoTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addHours: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'time'
    && deconstructed._type !== 'timeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addHours: invalid ISO datetime/time/duration string');
  }
  if(deconstructed._type === 'duration') {
    const returnValue = addMillisecondsToDuration(deconstructed, amount * 1000 * SECONDS_IN_HOUR);
    return constructDate(returnValue) as unknown as T;
  }
  if(deconstructed._type === 'time' || deconstructed._type === 'timeTz') {
    const returnValue = addMillisecondsToTime(deconstructed, amount * 1000 * SECONDS_IN_HOUR);
    return constructDate(returnValue) as unknown as T;
  }

  const {year, month, day, hours, minutes, seconds, milliseconds} = deconstructed;
  const dt = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds));
  dt.setUTCHours(dt.getUTCHours() + amount);
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  deconstructed.hours = dt.getUTCHours();
  return constructDate(deconstructed) as unknown as T;
}
